/**
 * Reports: saved questions about the ranch, immutable run snapshots, worklists made from them, and
 * exports. Reports never own facts; they read every module's records, facets and attributes.
 */
import { buildRegistry, defineModule } from "../../contract";
import { AGG_LABELS, ANIMAL_FIELDS, KNOWN_FACET_PATHS, OP_LABELS, fieldCatalog, filterPhrase } from "./shared/catalog";
import { DESCRIBE_MESSAGES } from "./shared/describe";
import { TEMPLATES } from "./shared/templates";
import { ISSUES, OPS, SUBJECTS, TOPIC_LABELS, type Grain } from "./shared/types";

export * from "./shared/types";

/** Messages the server sends that the web shows translated. */
const SERVER_MESSAGES = [
  "You don't have access to financial reports.", "We couldn't find that report.", "Only a manager can change someone else's report.",
  "That report isn't available on this ranch.", "Pick two runs of the same report.", "Pick two runs to compare.",
  "This report has no animals to put on a worklist.", "That kind of record doesn't fit this list.", "This report has no animals to keep as a list.",
  "We couldn't find that list.", "Fill this in to run the report.", "Choose a report or build one.",
];

/** Strings built at runtime (field labels, phrases, template names) so the catalog check sees them. */
function reportMessages(): string[] {
  const out = new Set<string>();
  const empty = buildRegistry([]);
  const grains: Partial<Record<(typeof SUBJECTS)[number], Grain[]>> = { events: ["event", "animal_event"], stock: ["lot", "item"], finance: ["doc", "line"] };
  for (const subject of SUBJECTS) {
    for (const grain of grains[subject] ?? [undefined]) {
      for (const spec of fieldCatalog(empty, subject, { canFinance: true, grain })) {
        out.add(spec.label); out.add(spec.section);
        if (spec.basis) out.add(spec.basis);
        for (const o of Object.values(spec.options ?? {})) out.add(o);
      }
    }
  }
  for (const spec of ANIMAL_FIELDS) if (spec.basis) out.add(spec.basis);
  for (const k of KNOWN_FACET_PATHS) { out.add(k.label); for (const o of Object.values(k.options ?? {})) out.add(o); }
  for (const op of OPS) for (const days of [undefined, 1, 2]) for (const hasDays of [false, true]) out.add(filterPhrase(op, hasDays, days));
  for (const t of TEMPLATES) {
    out.add(t.name); out.add(t.description);
    if (t.worklist) out.add(t.worklist);
    for (const p of t.params ?? []) out.add(p.label);
  }
  for (const label of Object.values(TOPIC_LABELS)) out.add(label);
  for (const key of [...DESCRIBE_MESSAGES, ...SERVER_MESSAGES]) out.add(key);
  for (const label of [...Object.values(OP_LABELS), ...Object.values(AGG_LABELS), ...Object.values(ISSUES)]) out.add(label);
  for (const key of ["{{field}}: how many", "{{field}}: last date", "{{field}}: first date", "{{field}}: latest", "{{field}}: first", "{{field}}: total", "{{field}}: average", "{{field}}: lowest", "{{field}}: highest", "Record details"]) out.add(key);
  return [...out];
}

export default defineModule({
  id: "reports",
  label: "Reports",
  description: "Saved reports, worklists, comparisons and exports.",
  jobs: [
    {
      type: "reports.worklist",
      label: "Worklist from a report",
      description: "Go down a report's animals and record the work for each one.",
      icon: "list",
      tone: "ops",
      workspace: "report-worklists",
      subjectKind: "animal",
      agent: { when: "Someone wants a report's animals turned into a list of work to do, such as weighing calves with no weaning weight." },
    },
  ],
  workspaces: [
    {
      id: "report-worklists",
      label: "Worklists from reports",
      description: "Lists of cattle to work, made from a report: weigh these, check those, find the rest.",
      icon: "list",
      group: "data",
      keywords: ["worklist", "worksheet", "report", "list", "print", "checklist", "missing", "report on these"],
      keywordsEs: ["lista de trabajo", "hoja de trabajo", "informe", "reporte", "lista", "imprimir", "pendientes"],
    },
  ],
  ranchSections: [
    {
      id: "reports",
      label: "Reports",
      description: "Saved reports, ready-made lists, past runs and spreadsheet downloads.",
      icon: "report",
      group: "reports",
      keywords: ["report", "reports", "inventory", "list", "export", "csv", "spreadsheet", "print", "worksheet", "count", "average", "summary"],
      keywordsEs: ["informe", "informes", "reporte", "reportes", "inventario", "lista", "exportar", "hoja de cálculo", "imprimir", "promedio", "resumen"],
    },
  ],
  messages: reportMessages(),
});
